import { runAggregationOnce } from '@/utils/aggregation';
import {
    buildAggregations,
    parseAggregationResults,
    createSignals,
    buildAppDataFromContext,
    getSubscriptionTimeZone,
} from './signals.utils.js';
import { ANONYMOUS_ONLY_SEGMENT_ID, SIGNAL_TYPES, SIGNAL_TYPE_ORDER } from './signals.constants.js';
import { getAppId } from '@/vuex/modules/trends/trends-settings.module.js';

const DEFAULT_SIGNAL_TYPES = [
    SIGNAL_TYPES.VISITOR_ADOPTION,
    SIGNAL_TYPES.VISITOR_ENGAGEMENT,
    SIGNAL_TYPES.VISITOR_RAGE_CLICKS,
    SIGNAL_TYPES.VISITOR_RETENTION,
    SIGNAL_TYPES.ACCOUNT_RETENTION,
    SIGNAL_TYPES.EVENT_ADOPTION,
];

const COUNTABLE_SIGNAL_TYPES = new Set([SIGNAL_TYPES.EVENT_ADOPTION]);

const SIGNAL_TYPES_UNSUPPORTED_FOR_ANONYMOUS = new Set([
    SIGNAL_TYPES.VISITOR_RETENTION,
    SIGNAL_TYPES.ACCOUNT_RETENTION,
]);

const signals = {
    namespaced: true,
    state: {
        signals: null,
        isLoading: null,
        loadingSignalTypes: [],
        activeFetch: 0,
        error: null,
    },
    mutations: {
        setSignals (state, { signals }) {
            state.signals = signals;
        },
        addSignals (state, { signals }) {
            state.signals = [...(state.signals ?? []), ...signals];
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setLoadingSignalTypes (state, { types }) {
            state.loadingSignalTypes = [...types];
        },
        removeLoadingSignalType (state, { type }) {
            state.loadingSignalTypes = state.loadingSignalTypes.filter((item) => item !== type);
        },
        setActiveFetch (state, { fetchId }) {
            state.activeFetch = fetchId;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchSignals ({ commit, state, rootGetters, dispatch }, { types } = {}) {
            const fetchId = state.activeFetch + 1;
            commit('setActiveFetch', { fetchId });
            commit('setLoading', { isLoading: true });
            commit('setSignals', { signals: [] });
            commit('setLoadingSignalTypes', { types: [] });
            commit('setError', { error: null });

            let context;
            let signalsToFetch;

            try {
                const requestedSignals = types?.length ? types : DEFAULT_SIGNAL_TYPES;

                const appId = await getAppId(rootGetters, dispatch);
                const hasShowAnonymousFlag = !!rootGetters['subscription/getFeatureFlag']('showAnonymous');
                const rawSegmentId = rootGetters['trendsSettings/getSegmentId'];
                const segmentId = rawSegmentId && rawSegmentId !== 'everyone' ? rawSegmentId : undefined;

                signalsToFetch = segmentId === ANONYMOUS_ONLY_SEGMENT_ID
                    ? requestedSignals.filter((type) => !SIGNAL_TYPES_UNSUPPORTED_FOR_ANONYMOUS.has(type))
                    : requestedSignals;

                const needsCountables = signalsToFetch.some((type) => COUNTABLE_SIGNAL_TYPES.has(type));
                if (needsCountables) {
                    await Promise.all([
                        dispatch('pages/loadAll', {}, { root: true }),
                        dispatch('features/loadAll', {}, { root: true }),
                        dispatch('trackTypes/loadAll', {}, { root: true }),
                    ]);
                }

                const countableIds = needsCountables
                    ? (rootGetters['trendsSettings/getEvents']?.map((event) => ({ id: event.id, kind: event.kind })) ?? [])
                    : [];

                context = {
                    appId,
                    hasShowAnonymousFlag,
                    countableIds,
                    segmentId,
                    appData: buildAppDataFromContext(rootGetters, appId),
                    timeZone: getSubscriptionTimeZone(rootGetters),
                };
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch signals' });
                commit('setLoading', { isLoading: false });
                throw error;
            }

            commit('setLoadingSignalTypes', { types: signalsToFetch });

            const allSignals = [];

            const fetchPromises = signalsToFetch.map(async (signalType) => {
                const [aggregations, breakpoints] = buildAggregations([signalType], context);
                const rawData = await Promise.all(
                    aggregations.map((agg) => runAggregationOnce(agg)),
                );

                if (state.activeFetch !== fetchId) return;

                const extractedData = parseAggregationResults([signalType], breakpoints, rawData, context);
                const newSignals = createSignals([signalType], extractedData, context);

                commit('addSignals', { signals: newSignals });
                commit('removeLoadingSignalType', { type: signalType });
                allSignals.push(...newSignals);
            });

            const results = await Promise.allSettled(fetchPromises);

            if (state.activeFetch !== fetchId) return allSignals;

            const failedResult = results.find((result) => result.status === 'rejected');

            if (failedResult) {
                const error = failedResult.reason;
                commit('setError', { error: error?.message || 'Failed to fetch signals' });
                commit('setLoading', { isLoading: false });
                throw error;
            }

            commit('setLoading', { isLoading: false });

            return allSignals;
        },
    },
    getters: {
        signals: (state) => {
            if (!state.signals?.length) return state.signals || [];

            return [...state.signals].sort((signalA, signalB) => {
                const orderA = SIGNAL_TYPE_ORDER.indexOf(signalA.type);
                const orderB = SIGNAL_TYPE_ORDER.indexOf(signalB.type);

                return orderA - orderB;
            });
        },
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
        loadingSignalTypes: (state) => state.loadingSignalTypes,
    },
};

export default signals;
